// frontend/src/pages/creator/CreatorVerification.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Upload, Loader2 } from 'lucide-react';
import CreatorLayout from './CreatorLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'ivisora_auth_token';

const STATUS_COPY = {
  none: { label: 'Not verified', tone: 'text-gray-500' },
  pending: { label: 'Under review', tone: 'text-amber-600' },
  verified: { label: 'Verified', tone: 'text-blue-600' },
  rejected: { label: 'Rejected', tone: 'text-red-600' },
};

const CreatorVerification = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idType, setIdType] = useState('passport');
  const [country, setCountry] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem(TOKEN_KEY);
    const r = await fetch(`${API_URL}/api/creator/verification`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setStatus(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setError('');
    if (!country.trim() || !idNumber.trim() || !front || !back) {
      setError('All fields and both ID images are required.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const fd = new FormData();
      fd.append('id_type', idType);
      fd.append('country', country);
      fd.append('id_number', idNumber);
      fd.append('id_front', front);
      fd.append('id_back', back);
      const r = await fetch(`${API_URL}/api/creator/verification`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Submission failed');
      setStatus(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const s = status?.status || 'none';
  const canSubmit = s === 'none' || s === 'rejected';

  return (
    <CreatorLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-blue-600" /> Verification
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Verification is <strong>optional</strong>. A verified badge builds buyer trust and gives your
        templates a small ranking boost. You can sell without it.
      </p>

      {loading ? (
        <div className="h-32 flex items-center justify-center"><div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
            <p className="text-sm text-gray-500">Current status</p>
            <p className={`text-lg font-bold ${STATUS_COPY[s].tone}`}>{STATUS_COPY[s].label}</p>
            {s === 'rejected' && status?.rejection_reason && (
              <p className="text-sm text-red-600 mt-1">Reason: {status.rejection_reason}</p>
            )}
            {s === 'pending' && <p className="text-sm text-gray-400 mt-1">We'll review your documents shortly.</p>}
            {status?.id_number_last4 && <p className="text-xs text-gray-400 mt-1">ID ending ••••{status.id_number_last4}</p>}
          </div>

          {canSubmit && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Submit verification</h2>
              {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">ID type</label>
                  <select value={idType} onChange={e => setIdType(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm">
                    <option value="passport">Passport</option>
                    <option value="national_id">National ID</option>
                    <option value="drivers_license">Driver's license</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
                  <input value={country} onChange={e => setCountry(e.target.value)} placeholder="e.g. US" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">ID number</label>
                  <input value={idNumber} onChange={e => setIdNumber(e.target.value)} placeholder="Only the last 4 digits are stored" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col items-center gap-2 px-4 py-6 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-violet-300">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-xs text-gray-500">{front ? front.name : 'ID front'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => setFront(e.target.files[0])} />
                  </label>
                  <label className="flex flex-col items-center gap-2 px-4 py-6 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-violet-300">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-xs text-gray-500">{back ? back.name : 'ID back'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => setBack(e.target.files[0])} />
                  </label>
                </div>
                <button onClick={submit} disabled={submitting} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : 'Submit for verification'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </CreatorLayout>
  );
};

export default CreatorVerification;
